/**
 * Enregistrement du service worker (PWA, C11).
 * - Production seulement : en développement, tout worker présent est désinscrit
 *   pour ne jamais servir un bundle périmé sur localhost:3000.
 * - `onUpdate` est appelé quand une nouvelle version attend (`registration.waiting`).
 *   La bannière (UpdateBanner) envoie alors { type: 'SKIP_WAITING' } et recharge.
 */
export interface RegisterOptions {
  onUpdate: (registration: ServiceWorkerRegistration) => void;
}

let started = false;

export function __resetForTests(): void { started = false; }

export async function unregisterAll(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map(r => r.unregister()));
}

function watchForWaiting(registration: ServiceWorkerRegistration, onUpdate: RegisterOptions['onUpdate']): void {
  if (registration.waiting) { onUpdate(registration); return; }
  const installing = registration.installing;
  if (installing) {
    installing.addEventListener('statechange', () => {
      if (installing.state === 'installed' && navigator.serviceWorker.controller) onUpdate(registration);
    });
  }
  registration.addEventListener('updatefound', () => {
    const next = registration.installing;
    if (!next) return;
    next.addEventListener('statechange', () => {
      if (next.state === 'installed' && navigator.serviceWorker.controller) onUpdate(registration);
    });
  });
}

export function registerServiceWorker({ onUpdate }: RegisterOptions): void {
  if (started || !('serviceWorker' in navigator)) return;
  started = true;
  if (process.env.NODE_ENV !== 'production') {
    void unregisterAll();
    return;
  }
  navigator.serviceWorker
    .register('/service-worker.js')
    .then(registration => watchForWaiting(registration, onUpdate))
    .catch(error => console.error('[PWA] Enregistrement du service worker impossible :', error));
}

/** Demande au worker en attente de prendre la main, puis recharge quand il contrôle la page. */
export function applyUpdate(registration: ServiceWorkerRegistration): void {
  const waiting = registration.waiting;
  if (!waiting) { window.location.reload(); return; }
  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true });
  waiting.postMessage({ type: 'SKIP_WAITING' });
}
